/**
 * Butterfly wallpapers — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_wallpapers_iv`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const butterflyWallpapersPack: ToilePack = {
    slug: 'butterfly-wallpapers',
    category: 'screens',
    name: 'Butterfly wallpapers',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 768, h: 1344},
    readings: [
        {key: 'morpho', label: 'Blue morpho', subject: 'a single blue morpho butterfly glowing on a black ground, its wings shimmering electric blue'},
        {key: 'swarm', label: 'Pink swarm', subject: 'a swarm of pink and purple butterflies rising from the bottom of the screen and thinning toward the top'},
        {key: 'monarch', label: 'Monarch', subject: 'an orange monarch butterfly resting on a purple flower with soft blurred greenery behind'},
    ],
    styles: [
        {key: 'macro', label: 'Macro photo', style: 'macro photograph look, sharp wing scales, tiny dew drops, soft blurred background', palette: 'vivid blue, pink and orange on soft dark', framing: 'Full-bleed, the butterflies in the lower two thirds'},
        {key: 'glow', label: 'Glowing art', style: 'digital art with a luminous glow, wings lit from inside, drifting sparkles, deep shadow', palette: 'electric blue, magenta and violet on black', framing: 'Full-bleed, the butterflies in the lower two thirds with dark sky above'},
        {key: 'watercolor', label: 'Watercolor', style: 'soft watercolor illustration, wet pigment edges, fine pencil veins, paper grain', palette: 'lilac, rose and powder blue on white paper', framing: 'Full-bleed, the butterflies in the lower two thirds with washes above'},
    ],
    directions: ['yellow butterfly', 'moths', 'butterfly garden'],
    directionHint: 'try: yellow butterfly',
    seo: {
        title: 'Butterfly Wallpapers: Blue Morpho, Pink Swarm, Monarch',
        metaDescription: 'Butterfly phone wallpapers in blue, pink and purple: a glowing blue morpho, a pink swarm and a monarch on a flower, in three styles.',
        h1: 'Butterfly wallpapers: a blue morpho, a pink swarm, a monarch',
        tagline: 'Nine light phone wallpapers, three butterfly scenes in three styles.',
        intro: 'Phone wallpapers of butterflies: a glowing blue morpho on black, a swarm of pink and purple butterflies rising from the bottom edge, and an orange monarch on a purple flower. Each comes as a macro photo look, a glowing digital art and a soft watercolour. People who want a light, pretty screen can set them as they are. Type a different butterfly, such as moths, and the styles carry it.',
        useCases: [
            'a phone lock screen',
            'a spring or summer refresh',
            'a gift for a nature lover',
            "a nursery or girls' room print",
        ],
        faq: [
            {q: 'What are the nine pictures?', a: 'Three butterfly scenes: a single blue morpho, a pink swarm rising, and a monarch on a flower. Each scene is shown in three styles.'},
            {q: 'Can I change the butterfly?', a: 'Yes. Type a subject such as a yellow butterfly, moths or a butterfly garden; a typed subject is a request, not a promised result.'},
            {q: 'How do the three styles differ?', a: 'Macro photo shows sharp wing scales and dew drops against a soft background. Glowing art has wings lit from inside on a dark ground. Watercolor is soft, with wet pigment edges and fine pencil veins.'},
            {q: 'Are these photographs?', a: 'No. The macro style has a photo look, but all nine pictures are rendered images.'},
        ],
    },
}
